import { useEffect, useState } from 'react'
import { optimisticRemoveKey, useApp, useWithoutRemoved } from '@/core/state/appStore'
import { ComboBox, toastError } from 'admination-react'
import { listMassnahmeModuln, listModulnCatalog, linkModul, type MassnahmeModulRow, type ModulRow } from '../../data/modul'
import { friendlyErrorMessage, isUniqueViolation } from '@/core/lib/dbError'

export function MassnahmeModuln({
  massnahmeId,
  organisationId,
  isOwner,
  version,
  onChanged,
}: {
  massnahmeId: string
  organisationId: string
  isOwner: boolean
  version: number
  onChanged: () => void
}) {
  const { state, dispatch } = useApp()
  const [moduln, setModuln] = useState<MassnahmeModulRow[]>([])
  const [catalog, setCatalog] = useState<ModulRow[]>([])
  const [newName, setNewName] = useState('')

  useEffect(() => {
    let cancelled = false
    listMassnahmeModuln(massnahmeId)
      .then((rows) => {
        if (!cancelled) setModuln(rows)
      })
      .catch((e) => console.error('Module laden fehlgeschlagen:', e))
    return () => {
      cancelled = true
    }
  }, [massnahmeId, version])

  useEffect(() => {
    if (!isOwner) return
    let cancelled = false
    listModulnCatalog(organisationId)
      .then((rows) => {
        if (!cancelled) setCatalog(rows)
      })
      .catch((e) => console.error('Modul-Katalog laden fehlgeschlagen:', e))
    return () => {
      cancelled = true
    }
  }, [organisationId, isOwner, version])

  const openDetails = (m: MassnahmeModulRow) => {
    const source = { kind: 'row' as const }
    dispatch({ type: 'OPEN_SCREEN', screen: { kind: 'modul', massnahmeId, modulId: m.modulId, tab: 'details', coachingId: null, source } })
  }

  const add = (name: string) => {
    const trimmed = name.trim()
    if (!trimmed) return
    void linkModul(massnahmeId, organisationId, trimmed, null)
      .then(() => {
        setNewName('')
        onChanged()
      })
      .catch((e) => {
        console.error('Modul verknüpfen fehlgeschlagen:', e)
        toastError(
          isUniqueViolation(e)
            ? 'Dieses Modul ist bei dieser Maßnahme schon dabei.'
            : friendlyErrorMessage(e, 'Verknüpfen fehlgeschlagen.'),
        )
      })
  }

  const shown = useWithoutRemoved(moduln, (m) => optimisticRemoveKey('modul-link', massnahmeId, m.modulId))

  if (shown.length === 0 && !isOwner) {
    return <p className="list-empty">Keine Module</p>
  }

  return (
    <div className="list list--cards">
      {shown.map((m) => {
        const selected = state.selectedMassnahmeId === massnahmeId && state.selectedModulId === m.modulId
        return (
          <div
            key={m.modulId}
            role="listitem"
            tabIndex={0}
            aria-selected={selected || undefined}
            className={selected ? 'list-row is-clickable is-selected' : 'list-row is-clickable'}
            onClick={() => openDetails(m)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault()
                e.currentTarget.click()
              }
            }}
          >
            <span className="list-row-title">{m.name}</span>
            {m.ueAnzahl != null && (
              <span className="list-row-props">
                <span>{m.ueAnzahl} UE</span>
              </span>
            )}
          </div>
        )
      })}
      {isOwner && (
        <div className="list-row">
          <ComboBox
            options={catalog.map((c) => c.name)}
            value={newName}
            onChange={setNewName}
            onCommit={add}
            placeholder="Modul hinzufügen…"
            aria-label="Modul hinzufügen"
          />
        </div>
      )}
    </div>
  )
}
